import { useEffect, useRef, useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { HealthCardHelper } from "@/Common/Helpers/HealthCareHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ExpressEditMode, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { HealthCardsEdit } from "@/Patient/Components/Profile/Identifiers/HealthCardsEdit";
import { OtherIdentifiersEdit } from "@/Patient/Components/Profile/Identifiers/OtherIdentifiersEdit";
import { Relative } from "../Models/Relative";
import { relativeStore } from "../Stores/RelativeStore";

interface IPatientProfileIdentifiersProps {
    autofocusOperation?: ExpressEditMode;
}

function IdentifiersEdit(props: IPatientProfileIdentifiersProps): JSX.Element {
    const relative = relativeStore.GetCurrent();
    const file = relativeStore.GetCurrentFile();

    const { getText } = useTranslation();

    const contactPointClones = MyleHelper.CloneObject<PatientIdentifier[]>(relative.HealthCards ?? []);
    const phoneClones = MyleHelper.CloneObject<PatientIdentifier[]>(relative.OtherPatientIdentifiers ?? []);

    const [identifiers, setIdentifiers] = useState(file.ActiveTabSavedState
        ? file.ActiveTabSavedState // Restore saved state
        : {
            healthCards: contactPointClones,
            otherIdentifiers: phoneClones
        });

    const identifiersRef = useRef(); // State is not available on component unmount

    useEffect(() => {
        identifiersRef.current = identifiers;
    }, [identifiers]);

    useEffect(
        () =>
            () => {
                // Save state on unmount
                sessionStore.dispatch(LocalOperations.Relative.SaveProfileState, { state: identifiersRef.current, tab: ProfileTab.Identifiers });
            }
        , [] // Empty array mean component unmount. We want the state saved once, not after every key stroke.
    );

    const onSave = (): void => {
        const updatedRelative = MyleHelper.CloneObject<Relative>(relative);
        updatedRelative.HealthCards = identifiers.healthCards;
        updatedRelative.OtherPatientIdentifiers = identifiers.otherIdentifiers;
        relativeStore.actions.updateIdentifiers(updatedRelative);
    };

    const onNamDateChange = (item: PatientIdentifier, value: Date): void => {
        const newIdentifiers = { ...identifiers };
        const healthcard: PatientIdentifier = newIdentifiers.healthCards.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        healthcard.ExpiryDate = HealthCardHelper.updateExpirationDate(item.ProvinceLookupId, value);
        setIdentifiers(newIdentifiers);
    };

    const onAddHealthCard = (): void => {
        const newIdentifiers = { ...identifiers };
        newIdentifiers.healthCards.push(HealthCardHelper.createEmptyHealthCard());
        setIdentifiers(newIdentifiers);
    };

    const OnOtherIdentifierNameChange = (item: PatientIdentifier, value: string): void => {
        const newIdentifiers = { ...identifiers };
        const otherIdentifier = newIdentifiers.otherIdentifiers.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        otherIdentifier.Name = value;
        setIdentifiers(newIdentifiers);
    };

    const OnOtherIdentifierNumberChange = (item: PatientIdentifier, value: string): void => {
        const newIdentifiers = { ...identifiers };
        const otherIdentifier = newIdentifiers.otherIdentifiers.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        otherIdentifier.Number = value;
        setIdentifiers(newIdentifiers);
    };

    const onRemoveHealthCard = (item: PatientIdentifier): void => {
        const newIdentifiers = { ...identifiers };
        newIdentifiers.healthCards = newIdentifiers.healthCards.filter(p => p.HealthIdentification_id !== item.HealthIdentification_id);
        setIdentifiers(newIdentifiers);
    };

    const OnAddOtherId = (): void => {
        const newIdentifiers = { ...identifiers };
        newIdentifiers.otherIdentifiers.push(HealthCardHelper.createEmptyOtherIdentifier());
        setIdentifiers(newIdentifiers);
    };

    const OnRemoveOtherId = (item: PatientIdentifier): void => {
        const newIdentifiers = { ...identifiers };
        newIdentifiers.otherIdentifiers = newIdentifiers.otherIdentifiers.filter(p => p.HealthIdentification_id !== item.HealthIdentification_id);
        setIdentifiers(newIdentifiers);
    };

    const onNamCountryChanged = (item: PatientIdentifier, selectedCountryId: string): void => {
        const newIdentifiers = { ...identifiers };
        const healthCard: PatientIdentifier = newIdentifiers.healthCards.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        const selectedCountry = cacheStore.GetLookupById(selectedCountryId);
        healthCard.CountryLookupId = selectedCountry.LookupID;
        healthCard.AvailableCountryRegionLookups = cacheStore.GetCountryRegionLookups(selectedCountry);
        healthCard.ProvinceLookupId = (healthCard.AvailableCountryRegionLookups && healthCard.AvailableCountryRegionLookups.length > 0) ? healthCard.AvailableCountryRegionLookups[0].LookupID : healthCard.CountryLookupId;
        healthCard.ExpiryDate = HealthCardHelper.updateExpirationDate(healthCard.ProvinceLookupId, healthCard.ExpiryDate);
        healthCard.IsValid = HealthCardHelper.isHealthCardValid(
            healthCard.Number,
            healthCard.ProvinceLookupId,
            healthCard.CountryLookupId,
            relative.FirstName,
            relative.LastName,
            relative.DateOfBirth,
            relative.GenderLookupId
        );
        setIdentifiers(newIdentifiers);
    };

    const onNamProvinceChanged = (item: PatientIdentifier, selectedProvinceId: string): void => {
        const newIdentifiers = { ...identifiers };
        const healthCard: PatientIdentifier = newIdentifiers.healthCards.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        const selectedProvince = cacheStore.GetLookupById(selectedProvinceId);
        healthCard.ProvinceLookupId = selectedProvince.LookupID;
        healthCard.ExpiryDate = HealthCardHelper.updateExpirationDate(selectedProvince.LookupID, healthCard.ExpiryDate);
        healthCard.IsValid = HealthCardHelper.isHealthCardValid(
            healthCard.Number,
            healthCard.ProvinceLookupId,
            healthCard.CountryLookupId,
            relative.FirstName,
            relative.LastName,
            relative.DateOfBirth,
            relative.GenderLookupId
        );
        setIdentifiers(newIdentifiers);
    };

    const onNamNumberChanged = (item: PatientIdentifier, number: string): void => {
        const newIdentifiers = { ...identifiers };
        const hc = newIdentifiers.healthCards.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        hc.Number = number;
        hc.IsValid = HealthCardHelper.isHealthCardValid(
            hc.Number,
            hc.ProvinceLookupId,
            hc.CountryLookupId,
            relative.FirstName,
            relative.LastName,
            relative.DateOfBirth,
            relative.GenderLookupId
        );
        setIdentifiers(newIdentifiers);
    };

    const onNamVersionChanged = (item: PatientIdentifier, version: string): void => {
        const newIdentifiers = { ...identifiers };
        const hc: PatientIdentifier = newIdentifiers.healthCards.find(h => h.HealthIdentification_id === item.HealthIdentification_id);
        hc.Version = version;
        setIdentifiers(newIdentifiers);
    };

    const isValid = (): boolean => {
        let isValid = true;
        identifiers.healthCards.forEach(x => {
            if (!x.IsValid || (IsNullOrWhiteSpace(x.Number.trim())))
                isValid = false;
        });
        return isValid;
    };

    return (
        <div className="tabComponent">
            <div className="tabComponent-main patient-prof patient-identifiers">
                <div className="block separated">
                    <div className="flex-container heading">
                        <h3 className="h thin-font">{getText("HealthCard")}</h3>
                    </div>
                    <div className="psingle-column wider centered">
                        <HealthCardsEdit
                            healthCards={identifiers.healthCards}
                            autofocusOperation={props.autofocusOperation}
                            onAddHealthCard={onAddHealthCard}
                            onRemoveHealthCard={onRemoveHealthCard}
                            onNamNumberChanged={onNamNumberChanged}
                            onNamCountryChanged={onNamCountryChanged}
                            onNamProvinceChanged={onNamProvinceChanged}
                            onNamVersionChanged={onNamVersionChanged}
                            onNamExpirationDateChange={onNamDateChange}
                        />
                    </div>
                </div>
                <div className="block separated">
                    <div className="flex-container heading">
                        <h3 className="h thin-font">{getText("OtherIdentifierslabel")}</h3>
                    </div>
                    <div className="psingle-column wider centered">
                        <OtherIdentifiersEdit
                            identifiers={identifiers.otherIdentifiers}
                            onAddOtherId={() => OnAddOtherId()}
                            onOtherIdentifierNameChange={(id, value) => OnOtherIdentifierNameChange(id, value)}
                            onOtherIdentifierNumberChange={(id, value) => OnOtherIdentifierNumberChange(id, value)}
                            onRemoveOtherId={id => OnRemoveOtherId(id)}
                        />
                    </div>
                </div>
            </div>
            <div className="tabComponent-footer ws-subfooter patient-profile">
                <div className="tb patient-profile-footer">
                    <div className="tbc pull-right">
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            onClick={() => sessionStore.dispatch(LocalOperations.Relative.CancelProfileEdition)}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            disabled={!isValid()}
                            className="btn btn-primary"
                            label={getText("SaveLabel")}
                            onClick={() => onSave()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export { IdentifiersEdit };
